import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SessionStorageService } from 'ngx-webstorage';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { completeEndpoint, EndpointPart, LOGIN_FALLBACK } from './core/global';
import { showNetworkTransferErrorMessage } from './services/booking/recourse-booking-functions';
import { NotificationServiceWrapper } from './services/notification-service-wrapper/notification-service-wrapper';
import { User } from './services/user-service/model/user.model';
import { UserService } from './services/user-service/user.service';
import { ConstantsService } from './shared/constants/constants.service';
import { NetworkActionEnum } from './shared/enums/network-action-enum';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly constantsService = inject(ConstantsService);
  private readonly userService = inject(UserService);
  private readonly notificationServiceWrapper = inject(NotificationServiceWrapper);
  private readonly translateService = inject(TranslateService);

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Get the User object from session storage
    const userObj: User = this.sessionStorageService.retrieve('user');

    let authReq;
    const multiPartRequest = req.url.includes(completeEndpoint(EndpointPart.POST_UPLOAD_ATTACHMENT));

    if (userObj) {
      // Clone the request and replace the original headers with
      // cloned headers, updated with the authorization.
      authReq = multiPartRequest
        ? req.clone({
            headers: req.headers
              .set(this.constantsService.get('HEADER_TOKEN_AUTH'), userObj.token)
              .set('Accept-Language', userObj.language),
          })
        : req.clone({
            headers: req.headers
              .set(this.constantsService.get('HEADER_TOKEN_AUTH'), userObj.token)
              .set('Accept-Language', userObj.language)
              .set('Content-Type', 'application/json'),
          });
    } else {
      authReq = multiPartRequest
        ? req.clone({
            headers: req.headers
              .set(this.constantsService.get('HEADER_TOKEN_AUTH'), '')
              .set('Accept-Language', this.constantsService.get('DEFAULT_LANGUAGE')),
          })
        : req.clone({
            headers: req.headers
              .set(this.constantsService.get('HEADER_TOKEN_AUTH'), '')
              .set('Accept-Language', this.constantsService.get('DEFAULT_LANGUAGE'))
              .set('Content-Type', 'application/json'),
          });
    }

    // send cloned request with header to the next handler.
    return next.handle(authReq).pipe(
      catchError(error => {
        // intercept the response error and displays it to the console
        // eslint-disable-next-line no-console
        console.log('Error Occurred');
        // eslint-disable-next-line no-console
        console.log(error);

        if (this.isUnauthorizedError(error.status)) {
          // eslint-disable-next-line no-console
          console.error(`Session has expired! Reason: ${error.message}`);
          // remove expired user token
          this.userService.logoutUser();
          // if dev or local, we can redirect to:
          // eslint-disable-next-line no-restricted-globals
          document.location.href = LOGIN_FALLBACK;
          return EMPTY; // return empty Observable, don't throw exceptions
        } else if (this.isForbiddenError(error.status)) {
          // eslint-disable-next-line no-console
          console.log('Error Occurred');
          //return empty();
        } else if (
          this.isBadRequestBusinessProcessError(error.status) &&
          error.error.errorCode &&
          error.error.errorCode === 8004
        ) {
          showNetworkTransferErrorMessage(
            this.notificationServiceWrapper,
            this.translateService,
            error,
            NetworkActionEnum.AuthenticityRecourseBookingError,
          );
        }

        // return the error to the method that called it
        return throwError(error);
      }),
    ) as any;
  }

  private isUnexpectedError(status: number): boolean {
    return status === this.constantsService.get('HTTP_STATUS_CODES').INTERNAL_SERVER_ERROR;
  }

  private isUnauthorizedError(status: number): boolean {
    return status === this.constantsService.get('HTTP_STATUS_CODES').UNAUTHORIZED;
  }

  private isForbiddenError(status: number): boolean {
    return status === this.constantsService.get('HTTP_STATUS_CODES').FORBIDDEN;
  }

  private isBadRequestBusinessProcessError(status: number): boolean {
    return status === this.constantsService.get('HTTP_STATUS_CODES').BAD_REQ_BUSINESS_PROCESS;
  }
}
